import { useState, useEffect } from 'react';
import type { RegionSourceOverlapResponse } from '../types/index.js';
import { apiGet } from '../lib/api.js';

export interface UseRegionSourceOverlapResult {
  overlap: RegionSourceOverlapResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * Spec 171 — region source-overlap read. Mirrors useVisibility's fetch/cancel
 * pattern. Returns the set overlap of CITED domains between the brand's monitored
 * regions (the "US/DK source ecosystems barely overlap" insight). Resolves to
 * `null` on error so the caller can simply not render the panel — this is an
 * additive insight, never load-bearing.
 */
export function useRegionSourceOverlap(
  tenantId: string | null,
  brandId: string | null,
): UseRegionSourceOverlapResult {
  const [overlap, setOverlap] = useState<RegionSourceOverlapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !brandId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiGet<RegionSourceOverlapResponse>(
      `/api/v1/tenants/${tenantId}/brands/${brandId}/region-source-overlap`,
    )
      .then((res) => {
        if (cancelled) return;
        setOverlap(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load region source overlap');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, brandId]);

  return { overlap, loading, error };
}
